import * as React from 'react'
import type { DescService } from '@bufbuild/protobuf'
import type { Client, Interceptor } from '@connectrpc/connect'
import { createClient } from '@connectrpc/connect'
import { createConnectTransport } from '@connectrpc/connect-web'
import { QueryClient } from '@tanstack/react-query'

import { recordDiagnostic } from '@modules/diagnostics/store'
import { parseStructuredError } from '@modules/error/errorCodes'

import { getToken } from '@company/auth/firebase'
import { apiHost } from '@company/config'

const authInterceptor: Interceptor = next => async req => {
  const token = await getToken()
  // @TODO: tokenなければキャンセル
  if (token) {
    // リクエストヘッダーにトークンをセットする
    req.header.set('Authorization', `Bearer ${token}`)
  }
  return await next(req)
}

/**
 * 失敗したRPCを診断パネルへ記録する（STGと開発環境のみ。既定は無効で何もしない）。
 *
 * 画面ごとのcatchに記録を足していく方式は採らない。companyだけでcatchを持つファイルが83あり
 * （共通ハンドラ `useHandleError` を使っているのは29）、必ず記録漏れが出る。transportは
 * react-query経由も含めて全RPCが通る唯一の場所なので、ここ1箇所で拾う。
 *
 * 記録するのは `parseStructuredError` を通した後の値だけ（生のrawMessageは載せない）。
 * 既存仕様openspec/specs/error-message-hiding「サーバー内部系codeのrawMessageは表示しない」を
 * 破らないため。エラーはそのまま再throwして、呼び出し側の扱いを変えない。
 */
export const diagnosticsInterceptor: Interceptor = next => async req => {
  try {
    return await next(req)
  } catch (error) {
    const structured = parseStructuredError(error)
    recordDiagnostic({
      kind: 'rpc',
      label: `${req.method.parent.name}/${req.method.name}`,
      code: String(structured.code),
      message: structured.message,
      traceId: structured.traceId,
    })
    throw error
  }
}

export const transport = createConnectTransport({
  baseUrl: apiHost,
  interceptors: [diagnosticsInterceptor, authInterceptor],
  useHttpGet: true,
})

/**
 * 認証ヘッダーなしの transport。
 * CompanyGuestService（無認証 RPC）専用。
 */
export const guestTransport = createConnectTransport({
  baseUrl: apiHost,
  useHttpGet: false,
  // ログイン前（パスワード再設定・LINEログイン開始）の失敗も診断パネルに載せる
  interceptors: [diagnosticsInterceptor],
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 3,
    },
  },
})

export function useClient<T extends DescService>(service: T): Client<T> {
  return React.useMemo(() => createClient(service, transport), [service])
}

/**
 * 無認証クライアント（CompanyGuestService 専用）。
 */
export function useGuestClient<T extends DescService>(service: T): Client<T> {
  return React.useMemo(() => createClient(service, guestTransport), [service])
}

export const cacheOptions = {
  // 5分はキャッシュする
  staleTime: 600000,
}
